import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight, VideoCamera, Article, FileText } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function getEmbedUrl(provider, url) {
  if (!url) return null;
  if (provider === "youtube") {
    const m = url.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{11})/);
    if (m) return `https://www.youtube.com/embed/${m[1]}`;
  }
  if (provider === "vimeo") {
    const m = url.match(/vimeo\.com\/(\d+)/);
    if (m) return `https://player.vimeo.com/video/${m[1]}`;
  }
  return null;
}

function KindIcon({ kind, size = 14 }) {
  if (kind === "video") return <VideoCamera size={size} weight="bold" />;
  if (kind === "post") return <FileText size={size} weight="bold" />;
  return <Article size={size} weight="bold" />;
}

export default function CMSTribune() {
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/cms/items?limit=6`).then((r) => setItems(r.data.items || [])).catch(() => {});
  }, []);

  if (!items.length) return null;

  return (
    <section className="border-y-2 border-black bg-[var(--paper)]" data-testid="cms-tribune-section">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="label-mono">§ Tribune éditoriale</div>
            <h2 className="font-serif text-4xl sm:text-5xl font-medium mt-2">Dernières publications</h2>
            <p className="text-[var(--muted)] mt-3 max-w-xl">
              Analyses, posts et vidéos signés par la rédaction VITA-LEX.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((it) => (
            <article
              key={it.id}
              data-testid={`cms-card-${it.slug}`}
              className="bg-[var(--paper)] border border-black p-6 flex flex-col cursor-pointer hover:bg-[var(--ink)] hover:text-[var(--paper)] transition-colors group"
              onClick={() => setActive(it)}
            >
              {it.cover_image_url && (
                <div className="aspect-[16/9] -m-6 mb-4 overflow-hidden border-b border-black/10">
                  <img src={it.cover_image_url} alt="" className="w-full h-full object-cover grayscale group-hover:grayscale-0" />
                </div>
              )}
              <div className="flex items-center gap-2 label-mono">
                <KindIcon kind={it.kind} />
                <span>{it.kind}</span>
                <span className="opacity-50">·</span>
                <span className="opacity-70">{it.cree_le?.slice(0, 10)}</span>
              </div>
              <h3 className="font-serif text-2xl font-medium mt-3 leading-tight">{it.title}</h3>
              {it.excerpt && <p className="text-sm mt-3 opacity-80 line-clamp-3">{it.excerpt}</p>}
              <div className="mt-auto pt-4 flex items-center gap-2 label-mono opacity-70 group-hover:opacity-100">
                Lire <ArrowRight size={14} weight="bold" />
              </div>
            </article>
          ))}
        </div>
      </div>

      {/* Reader modal */}
      {active && <CMSReader item={active} onClose={() => setActive(null)} />}
    </section>
  );
}

function CMSReader({ item, onClose }) {
  const embed = item.kind === "video" ? getEmbedUrl(item.video_provider, item.video_url) : null;
  const isUpload = item.video_provider === "upload";
  return (
    <div
      className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center overflow-y-auto p-4"
      onClick={onClose}
      data-testid="cms-reader-modal"
    >
      <div
        className="bg-[var(--paper)] w-full max-w-4xl my-12 border border-black"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-black bg-[var(--ink)] text-[var(--paper)]">
          <div className="label-mono flex items-center gap-2">
            <KindIcon kind={item.kind} /> {item.kind} · /{item.slug}
          </div>
          <button onClick={onClose} className="label-mono underline" data-testid="cms-reader-close">Fermer</button>
        </div>
        <div className="p-6 sm:p-10">
          <h1 className="font-serif text-3xl sm:text-4xl font-medium">{item.title}</h1>
          {item.excerpt && <p className="mt-3 text-lg italic text-[var(--muted)]">{item.excerpt}</p>}

          {item.kind === "video" && (
            <div className="mt-6 aspect-video bg-black">
              {embed && (
                <iframe
                  src={embed}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={item.title}
                />
              )}
              {isUpload && item.video_url && (
                <video src={`${BACKEND_URL}${item.video_url}`} controls className="w-full h-full">
                  <track kind="captions" />
                </video>
              )}
            </div>
          )}

          {item.body_markdown && (
            <div className="prose prose-stone mt-8 max-w-none whitespace-pre-wrap font-serif text-[15px] leading-relaxed">
              {item.body_markdown}
            </div>
          )}

          {!!item.tags?.length && (
            <div className="mt-8 flex flex-wrap gap-2">
              {item.tags.map((t) => (
                <span key={t} className="label-mono border border-black px-2 py-1">#{t}</span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
